import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { Company } from '../types/Company';
import { localStorageKeys } from '../utils/keys/localStorageKeys';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { paths } from '../pages/RouterContainer';

interface CompanyContextType {
  company: Company | null;
  changeCompany: (company: Company) => void;
}

const _initialContextData: CompanyContextType = {
  company: null,
  changeCompany: () => null,
};

export const CompanyContext = createContext<CompanyContextType>(_initialContextData);

/**
 * CompanyProvider for global state company management
 */
function CompanyProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [lsCurrentCompany, setLsCurrentCompany] = useLocalStorage<Company | null>(
    localStorageKeys.CURRENT_COMPANY_KEY,
    null,
  );
  const [currentCompany, setCurrentCompany] = useState<Company | null>(lsCurrentCompany);
  const changeCompany = useCallback(async (company: Company) => {
    setLsCurrentCompany(company);
    setCurrentCompany(company);
  }, []);

  // If company changes redirect to overview page
  useEffect(() => {
    // if (!currentCompany || currentCompany?.id === lsCurrentCompany?.id) return;
    const path = window.location.pathname;
    if (path.includes(paths.INVOICE)) navigate(paths.INVOICES);
    if (path.includes(paths.CODE_BOOK_ITEMS)) navigate(paths.CODE_BOOK_ITEMS);
    if (path.includes(paths.USER_TASKS)) navigate(paths.USER_TASKS);
    if (path.includes(paths.COMPANY_TEAMS + `/current`))
      navigate(`${paths.MY_COMPANIES}/edit/current`);
    if (path.includes(paths.MY_COMPANIES + `/current/`))
      navigate(`${paths.MY_COMPANIES}/edit/current`);
  }, [currentCompany]);

  useEffect(() => {
    if (isAuthenticated) return;
    setLsCurrentCompany(null);
    setCurrentCompany(null);
  }, [isAuthenticated]);

  return (
    <CompanyContext.Provider value={{ company: currentCompany, changeCompany }}>
      {children}
    </CompanyContext.Provider>
  );
}

export default CompanyProvider;

export const useCurrentCompany = () => {
  return useContext(CompanyContext);
};
